import { Box, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import moment from 'moment';
import * as React from 'react';
import { useMemo } from 'react';

import {
  DAY_GRAY,
  EmployeeCalendarDate,
  EmployeeCalendarEvent,
  EmployeeCalendarEventColors,
  WEEKEND_GRAY,
} from '~/components/EmployeeCalendar/data';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { useSxStyles } from './useStyles';

export type EmployeeCalendarCellGetters = {
  getEventContent?: (event: EmployeeCalendarEvent) => string;
  getEventColors?: (
    event: EmployeeCalendarEvent,
  ) => EmployeeCalendarEventColors | undefined;
};

type Props = {
  calendarDate: EmployeeCalendarDate;
  events: EmployeeCalendarEvent[];
  hideOutOfRange?: boolean;
} & EmployeeCalendarCellGetters;

export const EmployeeCalendarCell: React.FC<Props> = (props: Props) => {
  const {
    calendarDate,
    events,
    getEventContent,
    getEventColors,
    hideOutOfRange,
  } = props;

  const sxStyles = useSxStyles();

  const { cellStyles, content } = useMemo(() => {
    const { isWeekend, isToday, inRange, date } = calendarDate;

    const event = events.find((event) => {
      const { startDate, endDate } = event;

      return !!endDate
        ? moment(date).isBetween(
            moment(startDate),
            moment(endDate),
            'day',
            '[]',
          )
        : moment(date).isSameOrAfter(moment(startDate), 'day');
    });

    const weekDayStyle: SxProps = {
      backgroundColor: isWeekend ? WEEKEND_GRAY : DAY_GRAY,
    };
    const resolveEventColors = (): SxProps<Theme> | undefined => {
      if (!!event && inRange) {
        const resolvedEventColors: SxProps | undefined = !!getEventColors
          ? getEventColors(event)
          : undefined;

        return mergeSxProps(sxStyles.event, resolvedEventColors);
      }

      return undefined;
    };

    const isOutOfRangeStyle = (sx: SxProps) => {
      return !inRange
        ? hideOutOfRange
          ? sxStyles.hideOutOfRange
          : sx
        : undefined;
    };

    const cellStyles = {
      wrapper: mergeSxProps(
        sxStyles.dayCellWrapper,
        isToday ? sxStyles.todayCellWrapper : undefined,
        isOutOfRangeStyle(sxStyles.endOfRangeWrapper),
      ),
      cell: mergeSxProps(
        sxStyles.default,
        weekDayStyle,
        resolveEventColors(),
        isOutOfRangeStyle(sxStyles.endOfRange),
      ),
    };

    const content: string =
      !!event && inRange && !!getEventContent ? getEventContent(event) : '';

    return {
      event,
      cellStyles,
      content,
    };
  }, [
    events,
    sxStyles,
    getEventColors,
    getEventContent,
    calendarDate,
    hideOutOfRange,
  ]);

  return (
    <Box sx={cellStyles.wrapper}>
      <Box sx={cellStyles.cell}>{content}</Box>
    </Box>
  );
};
